import { api } from '../../services/api';
import { IData, IDataNoPagination } from '../../types/shared';

/**
 * Empresa ativa = mesma regra do Orquestrador (`orquestrador.py`, contrato 08/09):
 * `tb_empresa.ativa = 'Ativa'` E hoje dentro da janela de cliente
 * [`dt_cli_desde`, `dt_cli_ate`] (limite nulo = aberto). Excluída nem chega aqui.
 */
export type MotivoInativa =
  | 'inativa'
  | 'fora-dominio'
  | 'fora-acessorias'
  | 'saiu'
  | 'ainda-nao-cliente'
  | 'sem-situacao';

export interface ICamposDeSituacao {
  ativa?: string | null;
  dt_cli_desde?: string | null;
  dt_cli_ate?: string | null;
}

const hojeEmSaoPaulo = () =>
  new Date().toLocaleDateString('en-CA', { timeZone: 'America/Sao_Paulo' });

export function situacaoDaEmpresa(e: ICamposDeSituacao): {
  ativa: boolean;
  motivo?: MotivoInativa;
} {
  // rota antiga sem os campos: a empresa segue visível, como no Orquestrador
  if (e.ativa === undefined) return { ativa: true };
  const valor = (e.ativa ?? '').trim();
  if (valor !== 'Ativa') {
    if (valor === 'Inativa') return { ativa: false, motivo: 'inativa' };
    if (valor === 'ForaDaDominio') return { ativa: false, motivo: 'fora-dominio' };
    if (valor === 'ForaDoAcessorias') return { ativa: false, motivo: 'fora-acessorias' };
    return { ativa: false, motivo: 'sem-situacao' };
  }
  const hoje = hojeEmSaoPaulo();
  const desde = e.dt_cli_desde?.slice(0, 10);
  const ate = e.dt_cli_ate?.slice(0, 10);
  if (desde && desde > hoje) return { ativa: false, motivo: 'ainda-nao-cliente' };
  if (ate && ate < hoje) return { ativa: false, motivo: 'saiu' };
  return { ativa: true };
}

export const empresaAtiva = (e: ICamposDeSituacao) => situacaoDaEmpresa(e).ativa;

const dataBr = (iso?: string | null) =>
  iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}` : '';

export const MOTIVOS: Record<MotivoInativa, { rotulo: string; explica: string }> = {
  inativa: { rotulo: 'Inativa no cadastro', explica: 'Situação “Inativa” no cadastro da empresa.' },
  'fora-dominio': {
    rotulo: 'Fora da Domínio',
    explica: 'Não está mais na Domínio (situação “ForaDaDominio”).',
  },
  'fora-acessorias': {
    rotulo: 'Fora da Acessórias',
    explica: 'Não está mais na Acessórias (situação “ForaDoAcessorias”).',
  },
  saiu: { rotulo: 'Saiu do escritório', explica: 'A data de fim como cliente já passou.' },
  'ainda-nao-cliente': {
    rotulo: 'Ainda não é cliente',
    explica: 'A data de início como cliente ainda não chegou.',
  },
  'sem-situacao': { rotulo: 'Sem situação', explica: 'A situação no cadastro está vazia.' },
};

export function detalheDoMotivo(e: ICamposDeSituacao) {
  const { motivo } = situacaoDaEmpresa(e);
  if (motivo === 'saiu') return `Cliente até ${dataBr(e.dt_cli_ate)}`;
  if (motivo === 'ainda-nao-cliente') return `Cliente a partir de ${dataBr(e.dt_cli_desde)}`;
  return motivo ? MOTIVOS[motivo].explica : '';
}

export interface IEmpresaSituacao extends ICamposDeSituacao {
  id: number;
  nome: string;
  cnpj: string | null;
  external_id: string | null;
  grupo_de_empresas: string | null;
}

export interface IMudancaDeSituacao {
  id: number;
  empresa_id: number;
  empresa_nome: string;
  usuario_nome: string;
  acao: 'reativou' | 'inativou' | 'importacao';
  antes: string | null;
  depois: string | null;
  motivo: string | null;
  dt_criacao: string;
}

const enxuta = (e: any): IEmpresaSituacao => ({
  id: e.id,
  nome: e.nome,
  cnpj: e.cnpj,
  external_id: e.external_id,
  grupo_de_empresas: e.grupo_de_empresas,
  ativa: e.ativa ?? null,
  dt_cli_desde: e.dt_cli_desde ?? null,
  dt_cli_ate: e.dt_cli_ate ?? null,
});

export const situacaoApi = {
  /**
   * Todas as empresas com os campos de situação. Rota própria a construir no backend
   * (`/grupos-empresas/situacao/`); até lá, lê a listagem de empresas página a página.
   */
  todas: async (): Promise<{ empresas: IEmpresaSituacao[]; rotaPropria: boolean }> => {
    try {
      const res = await api.get<IDataNoPagination<IEmpresaSituacao[]>>(
        '/api/v1/grupos-empresas/situacao/',
      );
      return { empresas: res.data.data.map(enxuta), rotaPropria: true };
    } catch (e: any) {
      if (e?.response?.status !== 404 && e?.response?.status !== 405) throw e;
    }
    const pagina = (page: number) =>
      api.get<IData<IEmpresaSituacao[]>>('/api/v1/empresas/', { params: { page, size: 100 } });
    const primeira = await pagina(1);
    const { pages } = primeira.data.data;
    const resto = await Promise.all(
      Array.from({ length: Math.max(0, pages - 1) }, (_, i) => pagina(i + 2)),
    );
    const empresas = [primeira, ...resto].flatMap(r => r.data.data.items.map(enxuta));
    return { empresas, rotaPropria: false };
  },

  /** Rota a construir: reativa (ativa='Ativa' e/ou reabre a janela) e grava o log. */
  reativar: async (empresaId: number, motivo: string) =>
    api.post(`/api/v1/grupos-empresas/situacao/${empresaId}/reativar/`, { motivo }),

  /** Rota a construir: histórico de mudanças de situação (reativações, inativações, importações). */
  historico: async (empresaId?: number) =>
    (
      await api.get<IDataNoPagination<IMudancaDeSituacao[]>>(
        '/api/v1/grupos-empresas/situacao/historico/',
        { params: { empresa_id: empresaId } },
      )
    ).data.data,
};
